// The seller's Swish control on the payment stage — the Lovable mockup's SellerSwishActivation
// (components/dealflow/SellerSwishActivation.tsx) on the real backend. It sits under the waiting
// text and says nothing until the seller wants it:
//   not enabled — "Would you like to receive payment with Swish?" and an Enable button;
//   enable      — one field opens with Save and Cancel. If the seller's profile holds a number it is
//                 already written in the field; if not, the field is empty. Nothing is saved until
//                 the seller saves it;
//   enabled     — the confirmed number, with Change (the same field, prefilled, Cancel restores it);
//   locked      — the server no longer accepts a change (the buyer has reported, or it is confirmed).
// Every state is the server's: confirming calls ConfirmSwishNumber, and the buyer's QR code and Swish
// button appear from the state that call returns.
import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { paymentApi, paymentErrorCode } from "./paymentApi";
import { isSwedishMobile } from "./swishNumber";
import { META_CLASS } from "./PaymentParts";

// The server refusing because the payment moved on: the number can no longer change.
const LOCKING_CODES = ["paymentAlreadyReported", "paymentAlreadyConfirmed", "paymentStateChanged"];

export default function SellerSwishSetup({ state, onState, onRefresh, t }) {
  const contractId = state.contractId;
  const confirmed = state.sellerSwishNumber || null;
  // The number on the seller's profile, if it holds one: it fills the field when Swish is enabled.
  const suggested = state.suggestedSwishNumber || null;

  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);

  // The field starts from the confirmed number when it is being changed, from the profile number
  // when Swish is being enabled, and empty when there is neither. Keyed by that source, so a number
  // that changes underneath replaces an untouched draft. A starting number Swish cannot pay is
  // flagged at once rather than on leaving the field, since the seller has not typed it.
  const source = confirmed ?? suggested ?? "";
  const startDraft = () => ({ source, number: source, touched: source !== "" && !isSwedishMobile(source) });
  const [draft, setDraft] = useState(startDraft);
  const current = draft.source === source ? draft : startDraft();
  const valid = isSwedishMobile(current.number);
  const fieldError = current.touched && !valid ? t("payment.errors.recipientNotSwedishMobile") : "";
  const reset = () => setDraft(startDraft());

  const save = async (number) => {
    setPending(true);
    setError("");
    try {
      const next = await paymentApi.confirmSwishNumber(contractId, number.trim());
      onState(next);
      setEditing(false);
      return true;
    } catch (err) {
      const code = paymentErrorCode(err);
      setError(t(`payment.errors.${code}`, { defaultValue: t("payment.errors.generic") }));
      if (LOCKING_CODES.includes(code)) {
        setLocked(true);
        setEditing(false);
        onRefresh();
        return "locked";
      }
      return false;
    } finally {
      setPending(false);
    }
  };

  const open = () => { reset(); setError(""); setEditing(true); };
  const cancel = () => { reset(); setError(""); setEditing(false); };

  const submit = () => { if (valid && !pending) save(current.number); };
  const showSummary = !!confirmed && (!editing || locked);
  const lockedText = <p className={`${META_CLASS} m-0`} data-testid="seller-swish-locked">{t("payment.seller.swish.locked")}</p>;

  return (
    <div className="mt-6 border-t border-ds-rule-row pt-3.5" data-testid="seller-swish-activation">
      {showSummary ? (
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="seller-swish-recipient">
          <div>
            <p className={`${META_CLASS} m-0`}>{t("payment.seller.swish.number")}</p>
            <p className="m-0 text-[15px] font-medium tabular-nums text-ds-ink">{confirmed}</p>
          </div>
          {locked ? lockedText : (
            <button type="button" className="ds-action-text" onClick={open} data-testid="seller-swish-change">
              {t("payment.seller.swish.change")}
            </button>
          )}
        </div>
      ) : !editing ? (
        locked ? lockedText : (
          <>
            <p className={`${META_CLASS} m-0`}>{t("payment.seller.swish.optional")}</p>
            <button type="button" className="ds-action-text mt-2 h-auto p-0" onClick={open} data-testid="seller-swish-enable">
              {t("payment.seller.swish.enable")}
            </button>
          </>
        )
      ) : (
        <>
          <p className="m-0 text-sm font-semibold text-ds-ink">{t("payment.seller.swish.question")}</p>
          <p className={`${META_CLASS} mt-1`}>{t("payment.seller.swish.lead")}</p>
          <div className="mt-3 space-y-2.5">
            <label htmlFor="seller-swish-phone" className="ds-label block">{t("payment.seller.swish.number")}</label>
            <input
              id="seller-swish-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              className="ds-input"
              value={current.number}
              disabled={pending}
              aria-invalid={!!fieldError}
              aria-describedby={fieldError ? "seller-swish-error" : undefined}
              onBlur={() => setDraft({ ...current, touched: true })}
              onChange={(e) => setDraft({ ...current, number: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); submit(); }
                else if (e.key === "Escape") { e.preventDefault(); cancel(); }
              }}
              data-testid="seller-swish-input"
            />
            {fieldError && <p id="seller-swish-error" className="ds-error-text m-0">{fieldError}</p>}
            <div className="flex flex-wrap items-center gap-5">
              <button type="button" className="ds-action-ink ds-action-compact justify-center disabled:opacity-60" disabled={!valid || pending} onClick={submit} data-testid="seller-swish-save">
                {pending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {t("payment.seller.swish.save")}
              </button>
              <button type="button" className="ds-action-text" onClick={cancel} disabled={pending} data-testid="seller-swish-cancel">
                {t("actions.cancel")}
              </button>
            </div>
          </div>
        </>
      )}
      {error && <p className="ds-error-text m-0 mt-2.5" role="alert" data-testid="seller-swish-error">{error}</p>}
    </div>
  );
}
